import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ContactPicker from '../components/ContactPicker';
import DealPicker from '../components/DealPicker';
import {api} from '../../api';


export default function AddCrmTask() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ title: '', description: '', dueDate: '', status: 'TODO', priority: 'MEDIUM', dealId: '', contactIds: [], subtasks: [] });
  const [contacts, setContacts] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [newSubtask, setNewSubtask] = useState('');

  useEffect(() => {
    const contactId = searchParams.get('contactId');
    const dealId = searchParams.get('dealId');
    if (contactId) {
      api(`/contacts/${contactId}`).then(r => r.json()).then(c => {
        setContacts([{ id: c.id, firstName: c.firstName, lastName: c.lastName }]);
        setForm(f => ({ ...f, contactIds: [c.id] }));
      });
    }
    if (dealId) {
      api(`/deals/${dealId}`).then(r => r.json()).then(d => {
        setSelectedDeal({ id: d.id, name: d.name });
        setForm(f => ({ ...f, dealId: d.id }));
      });
    }
  }, [searchParams]);

  const handleContactChange = (selected) => {
    setContacts(selected);
    setForm({ ...form, contactIds: selected.map(c => c.id) });
  };

  const addSubtask = () => {
    if (!newSubtask.trim()) return;
    setForm({ ...form, subtasks: [...form.subtasks, { title: newSubtask.trim(), completed: false }] });
    setNewSubtask('');
  };

  const removeSubtask = (index) => {
    setForm({ ...form, subtasks: form.subtasks.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = { ...form, dealId: form.dealId || null, dueDate: form.dueDate || null };
    const res = await api(`/crm-tasks`, { method: 'POST', body: JSON.stringify(body) });
    if (res.ok) {
      const task = await res.json();
      navigate(`/crm/tasks/${task.id}`);
    }
  };

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">New Task</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.dueDate} onChange={(e) => setForm({...form, dueDate: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600">
                <option value="TODO">To Do</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="DONE">Done</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority *</label>
            <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal</label>
            <DealPicker selected={selectedDeal} onChange={(deal) => { setSelectedDeal(deal); setForm({...form, dealId: deal ? deal.id : ''}); }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contacts</label>
            <ContactPicker selected={contacts} onChange={handleContactChange} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtasks</label>
            {form.subtasks.length > 0 && (
              <ul className="space-y-2 mb-2">
                {form.subtasks.map((s, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50">{s.title}</span>
                    <button type="button" onClick={() => removeSubtask(i)} className="text-gray-400 hover:text-red-600">&times;</button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubtask(); } }}
                placeholder="Add a subtask..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
              <button type="button" onClick={addSubtask} disabled={!newSubtask.trim()} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Add</button>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => navigate('/crm/tasks')} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
